// Display · AmountDisplay
//
// Monetary amount renderer. Currency formatting and sign logic live here;
// every styled character flows through `TextValue` (or the leaf-style
// skeleton block when loading).

import { useFormat } from "@ds/display/useNumberFormat";
import {
  TONE,
  amountColor,
} from "@ds/tokens/palette";
import { TextValue, type TextSize, type TextWeight, type TextTone } from "@ds/display/TextValue";
import { Inline } from "@ds/layout/Inline";
import { Skeleton } from "@ds/display/Skeleton";
import { withExportSpec } from "@ds/display/exportSpec";

// ─── Inputs ───────────────────────────────────────────────────────────────

export type AmountSize = "sm" | "md" | "lg" | "hero";
export type AmountSign = "abs" | "actual" | "signed-positive";

export interface AmountDisplayInput {
  value: number | null | undefined;
  currency?: string | null;
  size?: AmountSize;
  colored?: boolean;
  strong?: boolean;
  secondary?: boolean;
  suffix?: string;
  sign?: AmountSign;
  delta?: boolean;
  signedNeutral?: boolean;
  loading?: boolean;
  testId?: string;
}

export type AmountDisplayProps = AmountDisplayInput;

// ─── Size mapping ─────────────────────────────────────────────────────────

interface SizeStyle {
  textSize: TextSize;
  weightDefault: TextWeight;
  weightStrong: TextWeight;
  skeletonWidth: number;
  skeletonHeight: number;
  /** Optional line-height for hero rendering. */
  lineHeight?: "display";
}

const SIZE: Record<AmountSize, SizeStyle> = {
  sm: {
    textSize:      "sm",
    weightDefault: "regular",
    weightStrong:  "medium",
    skeletonWidth:  84, skeletonHeight: 18,
  },
  md: {
    textSize:      "md",
    weightDefault: "regular",
    weightStrong:  "medium",
    skeletonWidth:  96, skeletonHeight: 20,
  },
  lg: {
    textSize:      "lg",
    weightDefault: "medium",
    weightStrong:  "semibold",
    skeletonWidth: 124, skeletonHeight: 24,
  },
  hero: {
    textSize:      "hero",
    weightDefault: "semibold",
    weightStrong:  "bold",
    skeletonWidth: 220, skeletonHeight: 40,
    lineHeight:    "display",
  },
};

// ─── Implementation ───────────────────────────────────────────────────────

export function AmountDisplay({
  value,
  currency,
  size = "md",
  colored = false,
  strong = false,
  secondary = false,
  suffix,
  sign = "abs",
  delta = false,
  signedNeutral = false,
  loading = false,
  testId,
}: AmountDisplayProps) {
  const { currency: formatCurrency } = useFormat();
  const sz = SIZE[size];
  const weight: TextWeight = strong ? sz.weightStrong : sz.weightDefault;

  if (loading) {
    return (
      <Skeleton
        width={sz.skeletonWidth}
        height={sz.skeletonHeight}
        testId={testId}
      />
    );
  }

  if (value == null) {
    return (
      <TextValue
        as="span"
        value="—"
        size={sz.textSize}
        weight={weight}
        tone="secondary"
        testId={testId}
      />
    );
  }

  // Resolve display value + prefix.
  let prefix = "";
  let displayValue: number;

  if (delta) {
    prefix = value >= 0 ? "+" : "−";
    displayValue = Math.abs(value);
  } else if (signedNeutral) {
    prefix = value >= 0 ? "+" : "−";
    displayValue = Math.abs(value);
  } else if (sign === "actual") {
    displayValue = value;
  } else if (sign === "signed-positive") {
    prefix = value > 0 ? "+" : value < 0 ? "−" : "";
    displayValue = Math.abs(value);
  } else {
    displayValue = Math.abs(value);
  }

  const formatted = formatCurrency(displayValue, currency);

  // Resolve tone for the value via shared Tone enum.
  let tone: TextTone | undefined;
  if (delta) {
    tone = "correction";
  } else if (signedNeutral) {
    tone = value >= 0 ? "green" : "red";
  } else if (colored) {
    // amountColor returns DOMAIN.revenue/cost/correction — map back to tone.
    const c = amountColor(value);
    if (c === TONE.green.base || value > 0) tone = "revenue";
    else if (c === TONE.red.base || value < 0) tone = "cost";
    else tone = "revenue";
  } else if (secondary) {
    tone = "muted";
  }

  const suffixTone: TextTone = signedNeutral
    ? (value >= 0 ? "green" : "red")
    : "muted";

  if (!suffix) {
    return (
      <TextValue
        as="span"
        size={sz.textSize}
        weight={weight}
        tone={tone}
        tabular
        nowrap
        lineHeight={sz.lineHeight}
        testId={testId}
      >
        {prefix}
        {formatted}
      </TextValue>
    );
  }

  return (
    <Inline as="span" inline gap="xs" align="baseline" testId={testId}>
      <TextValue
        as="span"
        size={sz.textSize}
        weight={weight}
        tone={tone}
        tabular
        nowrap
        lineHeight={sz.lineHeight}
      >
        {prefix}
        {formatted}
      </TextValue>
      <TextValue
        as="span"
        size={sz.textSize}
        tone={suffixTone}
      >
        {suffix}
      </TextValue>
    </Inline>
  );
}

// ─── Excel export ─────────────────────────────────────────────────────────
//
// A monetary cell exports as a native number carrying the row's own currency,
// never the "1 234,56 zł" string this component renders. `sign`/`suffix` are
// presentation-only and deliberately dropped — the exported figure is the
// signed truth so SUM() over the column is correct.

withExportSpec<typeof AmountDisplay, AmountDisplayProps>(AmountDisplay, {
  type: "currency",
  value: (p) => p.value,
  currency: (p) => p.currency,
});
